import * as z from 'zod';
import { individualCreditReportVendorSchema } from '../../enums.mjs';
import { fico } from '../opaque.mjs';

var Vendor;
(function (Vendor) {
    const experianString = z.coerce.string().nullish().catch(undefined);
    const experianObject = (schema) => schema.nullish().catch(undefined);
    const experianArray = (schema) => z.array(schema).nullish().catch(undefined);
    const looseShape = (shape) => z.looseObject(shape);
    Vendor.experianScoreFactorSchema = looseShape({
        importance: experianString,
        code: experianString,
    });
    Vendor.experianRiskModelSchema = looseShape({
        evaluation: experianString,
        grade: experianString,
        modelIndicator: experianString,
        score: experianString,
        scoreFactors: experianArray(Vendor.experianScoreFactorSchema),
        scorePercentile: experianString,
    });
    Vendor.experianPublicRecordSchema = looseShape({
        adjustmentPercent: experianString,
        amount: experianString,
        bankruptcyAssetAmount: experianString,
        bankruptcyVoluntaryIndicator: experianString,
        bookPageSequence: experianString,
        consumerComment: experianString,
        courtCode: experianString,
        courtName: experianString,
        disputeFlag: experianString,
        ecoa: experianString,
        evaluation: experianString,
        filingDate: experianString,
        plaintiffName: experianString,
        referenceNumber: experianString,
        repaymentPercent: experianString,
        status: experianString,
        statusText: experianString,
        statusDate: experianString,
    });
    Vendor.experianEnhancedPaymentDataSchema = looseShape({
        actualPaymentAmount: experianString,
        chargeoffAmount: experianString,
        ciiCode: experianString,
        complianceCondition: experianString,
        creditLimitAmount: experianString,
        enhancedAccountCondition: experianString,
        enhancedAccountType: experianString,
        enhancedPaymentHistory84: experianString,
        enhancedPaymentStatus: experianString,
        enhancedSpecialComment: experianString,
        enhancedTerms: experianString,
        enhancedTermsFrequency: experianString,
        firstDelinquencyDate: experianString,
        highBalanceAmount: experianString,
        maxDelinquencyCode: experianString,
        mortgageId: experianString,
        originalCreditorClassificationCode: experianString,
        originalLoanAmount: experianString,
        paymentLevelDate: experianString,
        purchasedPortfolioFromName: experianString,
        secondDelinquencyDate: experianString,
        secondaryAgencyCode: experianString,
        secondaryAgencyId: experianString,
        specialPaymentAmount: experianString,
        specialPaymentCode: experianString,
        specialPaymentDate: experianString,
    });
    Vendor.experianTrendedTradeSchema = looseShape({
        monthNumber: experianString,
        balanceAmount: experianString,
        originalLoanAmountOrLimit: experianString,
        scheduledPaymentAmount: experianString,
        actualPaymentAmount: experianString,
        lastPaymentDate: experianString,
    });
    Vendor.experianTradelineSchema = looseShape({
        accountNumber: experianString,
        accountType: experianString,
        accountTypeText: experianString,
        amount1: experianString,
        amount1Qualifier: experianString,
        amount2: experianString,
        amount2Qualifier: experianString,
        amountBalloonPayment: experianString,
        amountPastDue: experianString,
        balanceAmount: experianString,
        balanceDate: experianString,
        bankruptcyChapterNumber: experianString,
        consumerComment: experianString,
        consumerDisputeFlag: experianString,
        datePaymentDue: experianString,
        delinquencies30Days: experianString,
        delinquencies60Days: experianString,
        delinquencies90to180Days: experianString,
        derogCounter: experianString,
        ecoa: experianString,
        enhancedPaymentData: experianObject(Vendor.experianEnhancedPaymentDataSchema),
        evaluation: experianString,
        kob: experianString,
        lastPaymentDate: experianString,
        maxDelinquencyDate: experianString,
        monthlyPaymentAmount: experianString,
        monthlyPaymentType: experianString,
        monthsHistory: experianString,
        openDate: experianString,
        openOrClosed: experianString,
        originalCreditorName: experianString,
        paymentHistory: experianString,
        revolvingOrInstallment: experianString,
        soldToName: experianString,
        specialComment: experianString,
        status: experianString,
        statusText: experianString,
        statusDate: experianString,
        subscriberCode: experianString,
        subscriberName: experianString,
        terms: experianString,
        trendedTrades: experianArray(Vendor.experianTrendedTradeSchema),
    });
    Vendor.experianInquirySchema = looseShape({
        amount: experianString,
        date: experianString,
        kob: experianString,
        subscriberCode: experianString,
        subscriberName: experianString,
        terms: experianString,
        type: experianString,
        typeText: experianString,
    });
    Vendor.experianInformationalMessageSchema = looseShape({
        messageNumber: experianString,
        messageNumberDetailed: experianString,
        messageText: experianString,
    });
    Vendor.experianSummaryAttributeSchema = looseShape({
        id: experianString,
        value: experianString,
    });
    Vendor.experianSummarySchema = looseShape({
        summaryType: experianString,
        attributes: experianArray(Vendor.experianSummaryAttributeSchema),
    });
    Vendor.experianNameSchema = looseShape({
        firstName: experianString,
        generationCode: experianString,
        middleName: experianString,
        secondSurname: experianString,
        surname: experianString,
        type: experianString,
    });
    Vendor.experianPhoneSchema = looseShape({
        number: experianString,
        source: experianString,
        type: experianString,
    });
    Vendor.experianConsumerIdentitySchema = looseShape({
        name: experianArray(Vendor.experianNameSchema),
        phone: experianArray(Vendor.experianPhoneSchema),
    });
    Vendor.experianAddressInformationSchema = looseShape({
        censusGeoCode: experianString,
        city: experianString,
        countyCode: experianString,
        dwellingType: experianString,
        firstReportedDate: experianString,
        lastReportingSubscriberCode: experianString,
        lastUpdatedDate: experianString,
        msaCode: experianString,
        source: experianString,
        state: experianString,
        stateCode: experianString,
        streetName: experianString,
        streetPrefix: experianString,
        streetSuffix: experianString,
        timesReported: experianString,
        unitId: experianString,
        unitType: experianString,
        zipCode: experianString,
    });
    Vendor.experianEmploymentInformationSchema = looseShape({
        addressExtraLine: experianString,
        addressFirstLine: experianString,
        addressSecondLine: experianString,
        firstReportedDate: experianString,
        lastUpdatedDate: experianString,
        name: experianString,
        source: experianString,
        zipCode: experianString,
    });
    Vendor.experianFraudShieldSchema = looseShape({
        addressCount: experianString,
        addressDate: experianString,
        addressErrorCode: experianString,
        fraudShieldIndicators: experianObject(looseShape({
            indicator: experianArray(experianString),
        })),
        sic: experianString,
        socialCount: experianString,
        socialDate: experianString,
        socialErrorCode: experianString,
        text: experianString,
        type: experianString,
    });
    Vendor.experianStatementSchema = looseShape({
        dateReported: experianString,
        statementText: experianString,
        type: experianString,
    });
    Vendor.experianHeaderRecordSchema = looseShape({
        reportDate: experianString,
        reportTime: experianString,
        preamble: experianString,
        versionNo: experianString,
        mKeywordLength: experianString,
        mKeywordText: experianString,
        y2kReportedDate: experianString,
        inquiringSubcode: experianString,
        reportCode: experianString,
        applicationCode: experianString,
    });
    Vendor.experianMessageSchema = looseShape({
        messageNumber: experianString,
        messageText: experianString,
    });
    Vendor.experianTradeSummarySchema = looseShape({
        mortgageTradesCount: experianString,
        mortgageBalanceTotal: experianString,
        mortgageHighCreditTotal: experianString,
        mortgagePaymentsTotal: experianString,
        mortgagePastDueTotal: experianString,
        autoTradesCount: experianString,
        autoBalanceTotal: experianString,
        autoHighCreditTotal: experianString,
        autoPaymentsTotal: experianString,
        autoPastDueTotal: experianString,
        educationTradesCount: experianString,
        educationBalanceTotal: experianString,
        educationHighCreditTotal: experianString,
        educationPaymentsTotal: experianString,
        educationPastDueTotal: experianString,
        openTradesCount: experianString,
        openBalanceTotal: experianString,
        openHighCreditTotal: experianString,
        openPaymentsTotal: experianString,
        openPastDueTotal: experianString,
        revolvingTradesCount: experianString,
        revolvingBalanceTotal: experianString,
        revolvingHighCreditTotal: experianString,
        revolvingPaymentsTotal: experianString,
        revolvingPastDueTotal: experianString,
        otherTradesCount: experianString,
        otherBalanceTotal: experianString,
        otherHighCreditTotal: experianString,
        otherPaymentsTotal: experianString,
        otherPastDueTotal: experianString,
        otherInstallmentTradesCount: experianString,
        otherInstallmentBalanceTotal: experianString,
        otherInstallmentHighCreditTotal: experianString,
        otherInstallmentPaymentsTotal: experianString,
        otherInstallmentPastDueTotal: experianString,
        tradesCount: experianString,
        balanceTotal: experianString,
        highCreditTotal: experianString,
        paymentsTotal: experianString,
        pastDueTotal: experianString,
        securedDebtTotal: experianString,
        unsecuredDebtTotal: experianString,
        oldestTradeline: experianString,
        revolvingCreditUtilization: experianString,
        totalDebtHighCreditRatio: experianString,
    });
    Vendor.experianDerogatorySummarySchema = looseShape({
        chargeOffsCount: experianString,
        collectionsCount: experianString,
        bankruptciesCount: experianString,
        publicRecordsCount: experianString,
        get_30DayLates: experianString,
        get_60DayLates: experianString,
        get_90DayLates: experianString,
        inquiriesCount: experianString,
        mostRecentLateDate: experianString,
        disputesCount: experianString,
        otherCount: experianString,
    });
    Vendor.experianCustomDataSchema = looseShape({
        summaries: experianObject(looseShape({
            tradeSummary: experianObject(Vendor.experianTradeSummarySchema),
            derogatorySummary: experianObject(Vendor.experianDerogatorySummarySchema),
        })),
    });
    Vendor.experianCreditProfileSchema = looseShape({
        headerRecord: experianArray(Vendor.experianHeaderRecordSchema),
        addressInformation: experianArray(Vendor.experianAddressInformationSchema),
        consumerIdentity: experianObject(Vendor.experianConsumerIdentitySchema),
        employmentInformation: experianArray(Vendor.experianEmploymentInformationSchema),
        fraudShield: experianArray(Vendor.experianFraudShieldSchema),
        informationalMessage: experianArray(Vendor.experianInformationalMessageSchema),
        inquiry: experianArray(Vendor.experianInquirySchema),
        mla: experianObject(Vendor.experianMessageSchema),
        ofac: experianObject(Vendor.experianMessageSchema),
        summaries: experianArray(Vendor.experianSummarySchema),
        publicRecord: experianArray(Vendor.experianPublicRecordSchema),
        riskModel: experianArray(Vendor.experianRiskModelSchema),
        statement: experianArray(Vendor.experianStatementSchema),
        tradeline: experianArray(Vendor.experianTradelineSchema),
        customData: experianObject(Vendor.experianCustomDataSchema),
    });
    Vendor.experianCreditProfileWriteSchema = z.unknown();
    Vendor.reportSchema = z.object({
        frozen: z.boolean().nullish(),
        bankruptcy: z.boolean().nullish(),
        score: z.number().nullish(),
    });
    Vendor.creditReportInputFieldSchema = z.enum(["name", "address", "ssn"]);
    Vendor.vendorErrorSchema = z.object({
        statusCode: z.number().int().optional(),
        vendorCode: z.string().optional(),
    });
    Vendor.failureDetailSchema = Vendor.vendorErrorSchema.extend({
        missing: z.array(Vendor.creditReportInputFieldSchema).optional(),
    });
    const creditProfileSharedSchema = {
        bankruptcy: z.boolean().optional(),
        score: fico.optional(),
        failureDetail: Vendor.failureDetailSchema.optional().catch(undefined),
    };
    Vendor.meridianCreditProfileSchema = z.object({
        vendor: z.literal(individualCreditReportVendorSchema.enum.meridian),
        ...creditProfileSharedSchema,
    });
    Vendor.submittedIdentitySchema = z.object({
        firstName: z.string(),
        lastName: z.string(),
        street: z.string().optional(),
        zip: z.string().optional(),
    });
    Vendor.experianStoredCreditProfileSchema = z.object({
        vendor: z.literal(individualCreditReportVendorSchema.enum.experian),
        ...creditProfileSharedSchema,
        profile: Vendor.experianCreditProfileSchema.optional().catch(undefined),
        submitted: Vendor.submittedIdentitySchema.optional().catch(undefined),
    });
    Vendor.experianStoredCreditProfileWriteSchema = z.object({
        vendor: z.literal(individualCreditReportVendorSchema.enum.experian),
        ...creditProfileSharedSchema,
        profile: Vendor.experianCreditProfileWriteSchema.optional(),
        submitted: Vendor.submittedIdentitySchema.optional(),
    });
    Vendor.creditProfileSchema = z.discriminatedUnion("vendor", [
        Vendor.meridianCreditProfileSchema,
        Vendor.experianStoredCreditProfileSchema,
    ]);
    Vendor.creditProfileWriteSchema = z.discriminatedUnion("vendor", [
        Vendor.meridianCreditProfileSchema,
        Vendor.experianStoredCreditProfileWriteSchema,
    ]);
})(Vendor || (Vendor = {}));
var OFAC;
(function (OFAC) {
    (function (Vendor) {
        Vendor.reportSchema = z.object({
            hit: z.boolean(),
        });
    })(OFAC.Vendor || (OFAC.Vendor = {}));
})(OFAC || (OFAC = {}));

export { OFAC, Vendor };
